/**
 * Pure TEA update for collaborator / public share session mode.
 *
 * Shell: `useShareModeStore.ts` (Pinia + display lock Cmds).
 */
import { assertNever } from "../lib/assertNever";
import { just, nothing, type Maybe } from "../lib/maybe";
import type { UpdateFn } from "../lib/tea";

export type ShareRole = "read" | "comment" | "edit";

export type ShareModeModel = {
  readonly shareMode: boolean;
  readonly shareRole: Maybe<ShareRole>;
};

export const initialShareModeModel: ShareModeModel = {
  shareMode: false,
  shareRole: nothing,
};

export type ShareModeMsg =
  | { readonly type: "Enter"; readonly role: ShareRole }
  | { readonly type: "Exit" };

export type ShareModeCmd =
  | { readonly kind: "lockDisplay"; readonly role: ShareRole }
  | { readonly kind: "unlockDisplay" };

export function updateShareMode(
  _model: ShareModeModel,
  msg: ShareModeMsg,
): readonly [ShareModeModel, readonly ShareModeCmd[]] {
  switch (msg.type) {
    case "Enter":
      return [
        { shareMode: true, shareRole: just(msg.role) },
        [{ kind: "lockDisplay", role: msg.role }],
      ];
    case "Exit":
      return [initialShareModeModel, [{ kind: "unlockDisplay" }]];
    default:
      return assertNever(msg);
  }
}

export const updateShareModeFn: UpdateFn<
  ShareModeModel,
  ShareModeMsg,
  ShareModeCmd
> = updateShareMode;
